import React, { useRef, useState, useEffect } from 'react'
import CX from 'classnames'
import './fadingScrollContainerStyle.scss'

interface FadingScrollContainerProps {
  innerContainerRef: React.MutableRefObject<HTMLDivElement>
  hidden: boolean
}

// A horizontal scrolling container that has a nice fade effect on the x-axis when more content is available
export const FadingScrollContainer: React.FC<FadingScrollContainerProps> = ({ hidden, children }) => {
  const [contentOverflowing, setContentOverflowing] = useState(false)
  const [scrollRightSideReached, setScrollRightSideReached] = useState(false)
  const [scrollLeftSideReached, setScrollLeftSideReached] = useState(true)

  const scrollContainer = useRef<HTMLDivElement>()

  useEffect(() => {
    setContentOverflowing(scrollContainer?.current?.clientWidth !== scrollContainer?.current?.scrollWidth)
  }, [scrollContainer?.current?.scrollWidth, scrollContainer?.current?.clientWidth])

  const overflowClassName = React.useMemo(() => {
    let fadeClass = ''

    if (!scrollLeftSideReached && !scrollRightSideReached) {
      fadeClass = 'fade-both'
    } else if (scrollLeftSideReached) {
      fadeClass = 'right-fade'
    } else if (scrollRightSideReached) {
      fadeClass = 'left-fade'
    }

    return CX(contentOverflowing ? 'overflowing' : '', fadeClass)
  }, [contentOverflowing, scrollLeftSideReached, scrollRightSideReached])

  return (
    <div
      className={CX('fading-scroll-container', hidden ? '' : 'active', overflowClassName)}
      ref={scrollContainer}
      onScroll={e => {
        if (!contentOverflowing) return

        const el = e.currentTarget

        if (el.scrollLeft === 0) {
          setScrollLeftSideReached(true)
        } else {
          setScrollLeftSideReached(false)
        }

        const distanceFromRightSide = el.scrollWidth - el.scrollLeft - el.clientWidth
        if (distanceFromRightSide <= 1) {
          setScrollRightSideReached(true)
        } else {
          setScrollRightSideReached(false)
        }
      }}
    >
      {children}
    </div>
  )
}
